import { z } from 'zod'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { useInfiniteQueryRequest } from '~/ui/hooks/utils'

import { CursorSchema, MarketProductSchema } from './definitions'

const MarketProductListResponseSchema = z.object({
  data: z.array(MarketProductSchema),
  cursor: CursorSchema
})

export type MarketProductListResponse = z.infer<typeof MarketProductListResponseSchema>

async function fetchMarketProductList({
  accountId,
  cursor,
  limit = 100
}: {
  accountId: string
  cursor?: string
  limit?: number
}) {
  const { data } = await httpClientBanking({
    url: `/investments/accounts/${accountId}/storefront/offers`,
    method: 'GET',
    params: { cursor, limit }
  })

  const parsedData = MarketProductListResponseSchema.parse(data)

  return parsedData
}

export function getUseFetchMarketProductListKey(accountId: string, limit: number) {
  return ['useFetchMarketProductList', accountId, limit] as const
}

export function useFetchMarketProductList({ accountId, limit = 100 }: { accountId: string; limit?: number }) {
  const { data, ...rest } = useInfiniteQueryRequest<MarketProductListResponse, unknown, true>(
    getUseFetchMarketProductListKey(accountId, limit),
    ({ pageParam }) => fetchMarketProductList({ accountId, cursor: pageParam, limit }),
    {
      getNextPageParam: (lastPage: MarketProductListResponse) => lastPage.cursor.after,
      suspense: true
    }
  )

  return {
    data: data?.pages.flatMap(page => page.data) ?? [],
    ...rest
  }
}
